import { z } from 'zod'

export const COMPARISON_OPERATORS = [
  {
    label: '(<) is less than (number)',
    value: '<',
    rhsType: 'unary',
    type: 'number',
    enableCaseSensitive: false,
  },
  {
    label: '(<=) is less than or equal to (number)',
    value: '<=',
    rhsType: 'unary',
    type: 'number',
    enableCaseSensitive: false,
  },
  {
    label: '(=) is equal to (number)',
    value: '==',
    rhsType: 'unary',
    type: 'number',
    enableCaseSensitive: false,
  },
  {
    label: '(!=) is not equal to (number)',
    value: '!=',
    rhsType: 'unary',
    type: 'number',
    enableCaseSensitive: false,
  },
  {
    label: '(>) is greater than (number)',
    value: '>',
    rhsType: 'unary',
    type: 'number',
    enableCaseSensitive: false,
  },
  {
    label: '(>=) is greater than or equal to (number)',
    value: '>=',
    rhsType: 'unary',
    type: 'number',
    enableCaseSensitive: false,
  },
  {
    label: 'Exactly matches (string)',
    value: 'exact match',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Does not match (string)',
    value: 'does not exact match',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Equals (number or text)',
    value: 'number or text equals',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Does not equal (number or text)',
    value: 'number or text does not equal',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Contains (string)',
    value: 'contains',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Does not contain (string)',
    value: 'does not contain',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Starts with (string)',
    value: 'starts with',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Does not starts with (string)',
    value: 'does not start with',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Ends with (string)',
    value: 'ends with',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Does not end with (string)',
    value: 'does not end with',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Regex match (string)',
    value: 'regex match',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'Does not regex match (string)',
    value: 'does not regex match',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: true,
  },
  {
    label: 'is True (boolean)',
    value: 'is true',
    rhsType: 'none',
    type: 'boolean',
    enableCaseSensitive: false,
  },
  {
    label: 'is False (boolean)',
    value: 'is false',
    rhsType: 'none',
    type: 'boolean',
    enableCaseSensitive: false,
  },
  {
    label: 'Is in (list)',
    value: 'is in',
    rhsType: 'nary',
    type: 'list',
    enableCaseSensitive: true,
  },
  {
    label: 'Is not in (list)',
    value: 'is not in',
    rhsType: 'nary',
    type: 'list',
    enableCaseSensitive: true,
  },
  {
    label: 'Contains any of (list)',
    value: 'contains any of',
    rhsType: 'nary',
    type: 'list',
    enableCaseSensitive: true,
  },
  {
    label: 'array equals',
    value: 'array equals',
    rhsType: 'nary',
    type: 'list',
    enableCaseSensitive: true,
  },
  {
    label: 'array does not equal',
    value: 'array does not equal',
    rhsType: 'nary',
    type: 'list',
    enableCaseSensitive: true,
  },
  {
    label: 'Equals (date)',
    value: 'equals',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: false,
  },
  {
    label: 'Before (date)',
    value: 'before',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: false,
  },
  {
    label: 'After (date)',
    value: 'after',
    rhsType: 'unary',
    type: 'string',
    enableCaseSensitive: false,
  },
  {
    label: 'Between (date)',
    value: 'between',
    rhsType: 'binary',
    type: 'string',
    enableCaseSensitive: false,
  },
  {
    label: 'Exists',
    value: 'exists',
    rhsType: 'none',
    type: 'boolean',
    enableCaseSensitive: false,
  },
  //   {
  //     label: 'Is in destination',
  //     value: 'is in destination',
  //     rhsType: 'multi_select',
  //     type: 'destination',
  //   },
  //   {
  //     label: 'Select destination type',
  //     value: 'select destination type',
  //     rhsType: 'select',
  //     type: 'destinationType',
  //   },
]

interface OperatorInfo {
  rhsType: string
  type: string
  enableCaseSensitive: boolean
}

// 'contains any of' configs migrated from legacy may carry an empty entry —
// the legacy evaluator treated '' as matching every input — so those entries
// must round-trip through the editor rather than block save. Every other list
// operator keeps the non-empty rule.
export const listRhsSchema = (operatorValue?: string) => {
  const entry =
    operatorValue === 'contains any of'
      ? z.string()
      : z.string().trim().min(1, "'Compare with' value is required")
  return z.array(z.array(entry).min(1)).min(1, "'Compare with' value is required")
}

export function getOperatorInfo(operatorValue?: string): OperatorInfo | undefined {
  const operator = COMPARISON_OPERATORS.find((op) => op.value === operatorValue)
  if (!operator) return undefined

  return {
    rhsType: operator.rhsType,
    type: operator.type,
    enableCaseSensitive: operator.enableCaseSensitive,
  }
}
